import type { ComponentPublicInstance, Ref } from 'vue'
import { onUnmounted, ref } from 'vue'
import { useIntersectionObserver } from '@vueuse/core'
import { api } from '@/api'
import { CHAPTER_LOCK, CHARGE_TYPE } from '@/constants'
import { useUserStore } from '@/stores'
import { useFbqAddToCart } from '@/composables/fbq/useFbqAddToCart'

/**
 * 阅读埋点上报 Composable
 * 负责章节阅读相关的埋点上报
 */

interface ChapterTrackingOptions {
  contentId: Ref<string>
  chapterId: Ref<string>
  book: Ref<any>
  chapter: Ref<any>
  bookRead: (data: any) => void
}

export function useChapterTracking(options: ChapterTrackingOptions) {
  const {
    chapterId,
    book,
    chapter,
    bookRead,
  } = options

  const userStore = useUserStore()
  const startReadTime = ref(new Date().getTime())

  /**
   * 阅读埋点上报
   */
  function handleReadTrack() {
    if (!book.value || !chapter.value)
      return

    // 锁定章节不上报
    if (chapter.value?.lockStatus === CHAPTER_LOCK.LOCK)
      return

    bookRead({
      book_id: book.value.id,
      book_name: book.value.contentName,
      author: book.value?.authorInfo?.author,
      chapter_id: chapterId.value,
      chapter_versionid: chapter.value?.chapterVersionId,
      chapter_name: chapter.value?.contentChapterName,
      is_chargeable: chapter.value.feeType,
      serial_status: book.value.finishStatus === 0 ? 'ongoing' : 'completed',
      chapter_price: chapter.value.chargeType === CHARGE_TYPE.CHAPTER ? chapter.value?.priceAmount : 0,
      currency_coins: userStore.$state.coinBalance,
    })

    startReadTime.value = new Date().getTime()
  }

  /**
   * 用户激活上报
   */
  async function attributionTrack() {
    if (localStorage.getItem('IsActive'))
      return

    try {
      const res = await api.get<any, any>('/member/active')
      if (res.code === 0)
        localStorage.setItem('IsActive', '1')
    }
    catch (error: any) {
      // 静默失败
    }
  }

  /**
   * 页面关闭时的处理
   */
  function handleCloseEvent() {
    handleReadTrack()
    return true
  }

  // 组件卸载时上报
  onUnmounted(() => {
    handleReadTrack()
  })

  return {
    startReadTime,
    handleReadTrack,
    attributionTrack,
    handleCloseEvent,
  }
}

interface SubscribeCardLike {
  productId?: string | number
  subscribeConfigId?: string | number
  price?: number
  preferentialPrice?: number | null
}

/**
 * 滚动阅读器订阅卡片曝光埋点
 * 每张卡片首次可见（≥50%）时上报一次 FB AddToCart
 * 返回 bindRef 适合作为模板 :ref 函数
 */
export function useFbqSubscribeCardExposure() {
  const { reportAddToCart } = useFbqAddToCart()
  const reportedKeys = new Set<string>()
  const observers = new Map<string, () => void>()

  function bindRef(item: SubscribeCardLike, el: Element | ComponentPublicInstance | null) {
    const productKey = item.productId ?? item.subscribeConfigId
    if (productKey === undefined || productKey === '')
      return
    const key = String(productKey)
    if (!el) {
      const stop = observers.get(key)
      if (stop) {
        stop()
        observers.delete(key)
      }
      return
    }
    if (reportedKeys.has(key) || observers.has(key))
      return
    // Vue 2 在 HTML 元素的函数式 ref 上传的是真实 DOM；这里只是为了让 TS 通过组件实例分支
    const target = (el as Partial<Element>).nodeType ? (el as Element) : null
    if (!target)
      return
    const { stop } = useIntersectionObserver(
      target,
      ([{ isIntersecting }]) => {
        if (!isIntersecting || reportedKeys.has(key))
          return
        reportedKeys.add(key)
        reportAddToCart(productKey, 'Membership', item.preferentialPrice ?? item.price ?? 0)
        stop()
        observers.delete(key)
      },
      { threshold: 0.5 },
    )
    observers.set(key, stop)
  }

  return { bindRef }
}
